import type { FC, ReactNode } from 'react';
import { useCallback, useMemo } from 'react';

import { L12nText } from '../../../../../../../text';
import AdCircle from '../../assets/autoDecision/ad-circle.png';
import ChestEmeraldAutoDecision from '../../assets/CFT/autoDecision/CHEST-EMERALD.png';
import ChestRubyAutoDecision from '../../assets/CFT/autoDecision/CHEST-RUBY.png';
import ChestTopazAutoDecision from '../../assets/CFT/autoDecision/CHEST-TOPAZ.png';
import EmeraldAutoDecision from '../../assets/CFT/autoDecision/EMERALD.png';
import RubyAutoDecision from '../../assets/CFT/autoDecision/RUBY.png';
import TopazAutoDecision from '../../assets/CFT/autoDecision/TOPAZ.png';
import TrapAutoDecision from '../../assets/CFT/autoDecision/TRAP.png';
import ChestEmerald from '../../assets/CFT/CHEST-EMERALD.png';
import ChestRuby from '../../assets/CFT/CHEST-RUBY.png';
import ChestTopaz from '../../assets/CFT/CHEST-TOPAZ.png';
import Emerald from '../../assets/CFT/EMERALD.png';
import Ruby from '../../assets/CFT/RUBY.png';
import Topaz from '../../assets/CFT/TOPAZ.png';
import Trap from '../../assets/CFT/TRAP.png';

import styles from './CFT.module.css';

enum CFTBarrelTypes {
  gem = 'GEM',
  trap = 'TRAP',
  chest = 'CHEST',
  key = 'KEY',
}

interface CFTBarrel {
  autoDecision: string;
  barrelType: CFTBarrelTypes;
  won: string;
  gem?: string;
  gemCount?: string;
}

const CFT_ICONS: Record<string, string> = {
  ['CHEST-EMERALD']: ChestEmerald,
  ['CHEST-RUBY']: ChestRuby,
  ['CHEST-TOPAZ']: ChestTopaz,
  ['EMERALD']: Emerald,
  ['RUBY']: Ruby,
  ['TOPASZ']: Topaz,
};

const CFT_AUTO_ICONS: Record<string, string> = {
  ['CHEST-EMERALD']: ChestEmeraldAutoDecision,
  ['CHEST-RUBY']: ChestRubyAutoDecision,
  ['CHEST-TOPAZ']: ChestTopazAutoDecision,
  ['EMERALD']: EmeraldAutoDecision,
  ['RUBY']: RubyAutoDecision,
  ['TOPASZ']: TopazAutoDecision,
};
const gemType = {
  '1': 'TOPASZ',
  '2': 'EMERALD',
  '3': 'RUBY',
};

export const CFT: FC<{ barrels: CFTBarrel[] }> = ({ barrels = [] }) => {
  const isAutoDecision = useMemo(() => {
    return barrels.some((barrel) => barrel.autoDecision === 'true');
  }, [barrels]);

  const firstSkullIndex = useMemo(() => {
    return barrels.findIndex((playerDecision) => playerDecision.barrelType === CFTBarrelTypes.trap);
  }, [barrels]);

  const renderBarrel = useCallback(
    (barrel: CFTBarrel, index: number): ReactNode => {
      const { gemCount, barrelType, autoDecision, gem } = barrel;
      const isDisabled = index > firstSkullIndex;
      const barrelClass = `${styles.cftBarrel} ${isDisabled ? styles.cftBarrelDisable : ''}`;
      const isAuto = isAutoDecision && autoDecision === 'true';

      if (barrelType === CFTBarrelTypes.gem) {
        const gemIcon = gemType[gem as keyof typeof gemType];
        const icon = isAuto ? CFT_AUTO_ICONS[gemIcon] : CFT_ICONS[gemIcon];
        const multiplier = gemCount !== '1' ? gemCount : '';

        return (
          <div className={barrelClass} key={index}>
            <img src={icon} className={isAuto ? styles.cftGemImage : styles.cftAutoGemImage} alt={gemIcon} />
            <span className={styles.cftBarrelMultiplier}>{multiplier}</span>
          </div>
        );
      }

      if (barrelType === CFTBarrelTypes.trap) {
        const icon = isAuto ? TrapAutoDecision : Trap;

        return (
          <div className={barrelClass} key={index}>
            <img src={icon} className={isAuto ? styles.cftGemImage : styles.cftAutoGemImage} alt="Trap" />
          </div>
        );
      }

      return <div key={index}>{barrelType}</div>;
    },
    [firstSkullIndex, isAutoDecision]
  );

  return (
    <div className={`${styles.contentContainerMargin} ${isAutoDecision ? styles.contentAutoContainer : ''}`}>
      <div className={styles.contentContainer}>{barrels.map(renderBarrel)}</div>
      {isAutoDecision && (
        <span className={styles.autoDecisionContainer}>
          <img src={AdCircle} alt="Auto decision applied" />
          <L12nText tk="AUTO_DECISIONS_APPLIED" />
        </span>
      )}
    </div>
  );
};
